import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { universityService } from '../services/universityService';
import { RankedUniversity } from '../types/university.types';
import { Alert, Loader } from '../components/common';
import { getMediaUrl } from '../config/api.config';

export const RankingPage: React.FC = () => {
  const [universities, setUniversities] = useState<RankedUniversity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUniversities = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      
      const response = await universityService.getUniversitiesRanking();
      if (response.status === 'success' && response.data) {
        setUniversities(response.data);
      } else {
        setError('Impossible de récupérer le classement des universités');
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Une erreur est survenue lors de la récupération du classement');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchUniversities(true);
  };

  useEffect(() => {
    fetchUniversities();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader size="large" color="primary" text="Chargement du classement..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <Alert type="error" message={error} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl">

      <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
        <div className="mb-6">
          <div className="text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-secondary mb-3">Classement des universités</h1>
            <p className="text-base md:text-lg text-gray-600">Découvrez les universités les plus supportées dans cette édition</p>
          </div>
          <div className="flex justify-center mt-4">
            <button 
              onClick={handleRefresh} 
              disabled={isRefreshing}
              className="flex items-center px-4 py-2 bg-primary text-tertiary rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isRefreshing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-tertiary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Actualisation...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                  Actualiser le classement
                </>
              )}
            </button>
          </div>
        </div>

        {universities.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p className="text-lg text-gray-600">Aucune université disponible pour le moment.</p>
          </div>
        ) : (
          <>
            {/* Podium pour les 3 premiers - visible sur tous les écrans */}
            <div className="mb-6 grid grid-cols-3 gap-1 max-w-xl mx-auto">
              {/* 2ème place */}
              <div className="order-1 pt-3">
                {universities.length > 1 && (
                  <div className="flex flex-col items-center">
                    <div className="relative mb-1">
                      <div className="absolute -top-2 -right-2 bg-gray-300 text-white w-6 h-6 rounded-full flex items-center justify-center font-bold shadow-md text-xs">2</div>
                      <img 
                        src={getMediaUrl(universities[1]?.logo)} 
                        alt={universities[1]?.name} 
                        className="w-12 h-12 md:w-16 md:h-16 rounded-full border-3 border-gray-300 object-cover"
                      />
                    </div>
                    <div className="h-16 md:h-20 bg-gray-200 w-full rounded-t-lg flex flex-col items-center justify-end p-1">
                      <div className="text-center w-full px-1">
                        <h3 className="font-bold text-xs line-clamp-1" title={universities[1]?.name}>{universities[1]?.name}</h3>
                        <p className="text-xs font-semibold">{universities[1]?.supporters_count}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* 1ère place */}
              <div className="order-2">
                {universities.length > 0 && (
                  <div className="flex flex-col items-center">
                    <div className="relative mb-1">
                      <div className="absolute -top-2 -right-2 bg-yellow-500 text-white w-6 h-6 rounded-full flex items-center justify-center font-bold shadow-md text-xs">1</div>
                      <img 
                        src={getMediaUrl(universities[0]?.logo)} 
                        alt={universities[0]?.name} 
                        className="w-14 h-14 md:w-20 md:h-20 rounded-full border-3 border-yellow-500 object-cover"
                      />
                    </div>
                    <div className="h-20 md:h-24 bg-yellow-100 w-full rounded-t-lg flex flex-col items-center justify-end p-1">
                      <div className="text-center w-full px-1">
                        <h3 className="font-bold text-xs md:text-sm line-clamp-1" title={universities[0]?.name}>{universities[0]?.name}</h3>
                        <p className="text-xs font-semibold">{universities[0]?.supporters_count}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* 3ème place */}
              <div className="order-3 pt-5">
                {universities.length > 2 && (
                  <div className="flex flex-col items-center">
                    <div className="relative mb-1">
                      <div className="absolute -top-2 -right-2 bg-amber-700 text-white w-6 h-6 rounded-full flex items-center justify-center font-bold shadow-md text-xs">3</div>
                      <img 
                        src={getMediaUrl(universities[2]?.logo)} 
                        alt={universities[2]?.name} 
                        className="w-10 h-10 md:w-14 md:h-14 rounded-full border-3 border-amber-700 object-cover"
                      />
                    </div>
                    <div className="h-14 md:h-16 bg-amber-100 w-full rounded-t-lg flex flex-col items-center justify-end p-1">
                      <div className="text-center w-full px-1">
                        <h3 className="font-bold text-xs line-clamp-1" title={universities[2]?.name}>{universities[2]?.name}</h3>
                        <p className="text-xs font-semibold">{universities[2]?.supporters_count}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Liste complète du classement */}
            <div className="bg-gray-50 rounded-xl p-3 md:p-4">
              <h2 className="text-xl font-bold text-secondary mb-3 px-1">Classement complet</h2>
              
              <div className="space-y-2">
                {universities.map((university, index) => (
                  <div 
                    key={university.id} 
                    className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden ${index < 3 ? 'border-l-4 ' + (index === 0 ? 'border-yellow-500' : index === 1 ? 'border-gray-300' : 'border-amber-700') : ''}`}
                  >
                    <Link 
                      to={`/universites/${university.id}`}
                      className="p-2 py-3 flex items-center gap-2 w-full transition-colors hover:bg-gray-50"
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-lg flex-shrink-0 ${
                        index === 0 ? 'bg-yellow-100 text-yellow-600' : 
                        index === 1 ? 'bg-gray-100 text-gray-600' : 
                        index === 2 ? 'bg-amber-100 text-amber-800' : 
                        'bg-blue-50 text-blue-600'
                      }`}>
                        {university.rank}
                      </div>
                      
                      <div className="flex-shrink-0">
                        <img 
                          className="h-10 w-10 rounded-full object-cover border border-gray-200" 
                          src={getMediaUrl(university.logo)} 
                          alt={university.name} 
                        />
                      </div>
                      
                      <div className="min-w-0 flex-1 ml-1">
                        <h3 className="text-sm font-medium text-gray-900 line-clamp-2" title={university.name}>
                          {university.name}
                        </h3>
                        <div className="flex items-center">
                          <span className="text-xs text-gray-600">
                            <span className="font-bold">{university.supporters_count}</span> supporters
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex-shrink-0">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                        </svg>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
